import "./style.scss";

import KeyboardArrowDownSharpIcon from "@mui/icons-material/KeyboardArrowDownSharp";
import { Button } from "@mui/material";
import AdvanceCustomStatusField from "components/AdvanceManagement/AdvanceCustomStatusField";
import { validateAdvanceSlicePaymentDate } from "components/AdvanceManagement/utils/advanceSliceAmounts";
import {
    advanceSliceDisabledTooltipKey,
    advanceSlicePeriodWarningKey,
    getAdvanceSliceLock,
    getSlicePaymentStatusStyles,
    paymentStatusForNewSliceDate,
} from "components/AdvanceManagement/utils/advanceSliceFieldRules";
import CurrencyIcon from "components/shared/CurrencyIcon";
import NumberFormComponent from "components/shared/FormComponent/NumberFormComponent";
import { StyledDatePicker } from "components/shared/StyledDatePicker";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import { useMemo } from "react";
import type { FieldArrayWithId } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { DTOCreateAdvancePaymentSlice } from "store/advancePaymentSlice";
import { useAppSelector } from "store/store";
import { PaymentStatus } from "store/types";
import type { Binary } from "types";

import NotAllowHoriz from "../../../../assets/img/svg/notAllowHor";
import variables from "../../../../assets/styles/_variables.module.scss";
import type { TAdvancePayment } from "..";

export interface IUpdateBalanceForm {
    amount: number | null;
    createAdvance: null | 0 | 1;
    slices: DTOCreateAdvancePaymentSlice[];
    comment: string | null;
}

interface IAdvanceFieldsLine {
    slice: FieldArrayWithId<TAdvancePayment | IUpdateBalanceForm, "slices", "id">;
    index: number;
    fields: FieldArrayWithId<TAdvancePayment | IUpdateBalanceForm, "slices", "id">[];
    useFormData: any;
    removeRow: (index: number) => void;
    maxWidth?: string;
    /**
     * Defaults to false if not update + no connectedTPD.
     * Needed to disable slice if on closed period and paid.
     */
    isPeriodClosed?: boolean;
    slicesLength: number;
    isUpdate?: boolean;
    /** false for "advance", true for "package" (forfait) — hide period warning for packages. */
    isForfait?: Binary;
    /**
     * Compact mode: positions the slice index and delete button outside the flex flow using
     * absolute positioning, and reduces the width of the amount/date fields to give more room
     * to the comment field. Use when horizontal space is limited (e.g. term form).
     */
    compact?: boolean;
    /**
     * Read-only mode: all fields are disabled and the delete button is hidden.
     * Used in edit mode of the term form where advance payments cannot be modified here.
     */
    readOnly?: boolean;
    /**
     * Earliest selectable payment date (term form: title not published before the contract period).
     */
    minPaymentDate?: Date;
}

const AdvanceFieldsLine = ({
    slice,
    index,
    useFormData,
    fields,
    removeRow,
    // handleChangeDate,
    maxWidth,
    isPeriodClosed = false,
    slicesLength,
    isUpdate = false,
    isForfait = 0,
    compact = false,
    readOnly = false,
    minPaymentDate,
}: IAdvanceFieldsLine) => {
    const [t] = useTranslation();
    const { control, watch, errors, setValue, trigger } = useFormData;
    const { periods, activePeriodData } = useAppSelector((state) => state.period);

    const paidStatus = watch(`slices.${index}.status`) as PaymentStatus;
    const paymentDate = watch(`slices.${index}.paymentDate`);
    const slicePaymentStatusStyles = getSlicePaymentStatusStyles(paidStatus ?? PaymentStatus.NOT_PAID);
    const sliceLock = getAdvanceSliceLock(
        { sliceId: slice["sliceId"], status: slice.status },
        isUpdate,
        readOnly,
        isPeriodClosed,
    );
    const { isDisabled } = sliceLock;
    const disabledTooltipKey = advanceSliceDisabledTooltipKey({
        readOnly,
        isPeriodClosed,
        isUpdate,
        ...sliceLock,
    });
    const disabledTooltipText = disabledTooltipKey ? t(disabledTooltipKey) : "";

    const handleChangeDate = (event: { name: string; value: Date }) => {
        if (slice["sliceId"]) return;
        const rowIndex = +event.name.split(".")[1];
        setValue(`slices.${rowIndex}.status`, paymentStatusForNewSliceDate(event.value));
    };

    const slicePeriodWarning = useMemo(
        () =>
            advanceSlicePeriodWarningKey({
                isDisabled,
                isForfait,
                paymentDate,
                periods,
                activePeriod: activePeriodData,
            }),
        [isDisabled, isForfait, paymentDate, periods, activePeriodData],
    );

    const getAmountError = () => {
        const fieldError = errors.slices?.[index]?.amount;
        if (!fieldError) return undefined;
        // In compact mode show only the red border, not the error message
        if (compact) return { type: fieldError.type };
        return fieldError;
    };

    return (
        <div
            className={`single-slice-outer${compact ? " single-slice-outer--compact" : ""}`}
            key={slice.id}
            style={{ maxWidth }}
        >
            {/* Compact mode: slice index rendered absolutely above the row */}
            {compact && slicesLength > 1 && (
                <div className="slice-index slice-index--compact">
                    <span>{`${index + 1}/${slicesLength}`}</span>
                </div>
            )}

            <div className="single-slice">
                {/* Normal mode: slice index rendered inline in the flex row */}
                {!compact && slicesLength > 1 && (
                    <div className="slice-index">
                        <span>{`${index + 1}/${slicesLength}`}</span>
                    </div>
                )}
                <NumberFormComponent
                    label=""
                    control={control}
                    placeholder={compact ? "0" : t("AdvancePayment.Amount")}
                    controlName={`slices.${index}.amount`}
                    error={getAmountError()}
                    float={true}
                    icon={<CurrencyIcon sx={{ fontSize: 16 }} />}
                    required
                    disabled={isDisabled}
                    disabledText={disabledTooltipText}
                    sx={{ minWidth: compact ? "120px" : "196px", maxWidth: compact ? "120px" : undefined }}
                />
                <div className={`flex flex-col ${compact ? "min-w-[140px] max-w-[140px]" : "min-w-[203px]"}`}>
                    <Controller
                        name={`slices.${index}.paymentDate`}
                        control={control}
                        rules={{
                            onChange: (e) => {
                                handleChangeDate(e.target);
                            },
                            validate: (value) => {
                                if (paidStatus !== PaymentStatus.NOT_PAID && !value)
                                    return t("AdvancePayment.Required");
                                return validateAdvanceSlicePaymentDate(value, minPaymentDate, {
                                    invalidDate: t("AdvancePayment.InvalidDate"),
                                    beforePeriodStart: t("AdvancePayment.PaymentDateBeforePeriodStart"),
                                });
                            },
                        }}
                        render={({ field, formState: { errors } }) => {
                            return (
                                <StyledDatePicker
                                    {...field}
                                    inputFormat="dd/MM/yy"
                                    disabled={isDisabled}
                                    disabledText={disabledTooltipText}
                                    maxYearDistance={2}
                                    minDate={minPaymentDate}
                                    helperText={
                                        (errors.slices &&
                                            errors?.slices[index]?.paymentDate?.type === "required" &&
                                            errors?.slices[index]?.paymentDate?.message) ||
                                        (errors.slices &&
                                            errors?.slices[index]?.paymentDate?.type === "validate" &&
                                            errors?.slices[index]?.paymentDate?.message)
                                    }
                                    textFieldSx={{
                                        ".MuiFormHelperText-root": {
                                            color: "rgb(211, 47, 47)",
                                            marginLeft: "0",
                                        },
                                        ".MuiSvgIcon-root": {
                                            fontSize: "1.2rem",
                                        },
                                        minWidth: compact ? "140px" : "203px",
                                    }}
                                />
                            );
                        }}
                    />
                </div>
                <Controller
                    name={`slices.${index}.comment`}
                    control={control}
                    render={({ field }) => {
                        return (
                            <StyledTextField
                                {...field}
                                placeholder={t("AdvancePayment.Comment")}
                                name="comment"
                                style={{ flex: "1", minWidth: 0 }}
                                disabled={isDisabled}
                                disabledText={disabledTooltipText}
                            />
                        );
                    }}
                />
                <AdvanceCustomStatusField
                    control={control}
                    controlName={`slices.${index}.customStatusId`}
                    disabled={readOnly || isPeriodClosed}
                    sx={{ minWidth: compact ? "140px" : "160px", height: "36px" }}
                />
                <Controller
                    control={control}
                    name={`slices.${index}.status`}
                    render={({ field }) => {
                        return (
                            <StyledSelect
                                {...field}
                                onChange={(e) => {
                                    field.onChange(e);
                                    void trigger(`slices.${index}.paymentDate`);
                                }}
                                IconComponent={KeyboardArrowDownSharpIcon}
                                buttonType
                                noGradient
                                disabled={slice["sliceId"] ? true : isDisabled} // They cannot change the status through here if slice already exists. They must do it through the main action.
                                tooltipText={disabledTooltipText}
                                sx={{
                                    "&.MuiOutlinedInput-root": {
                                        fontSize: "13px",
                                        ".MuiOutlinedInput-input": {
                                            padding: "10px 14px",
                                            cursor: isDisabled ? "not-allowed" : "pointer",
                                        },
                                        ".MuiSvgIcon-root": {
                                            color: slicePaymentStatusStyles.color,
                                        },
                                        "& > fieldset": {
                                            border: "none",
                                        },
                                        "&:hover": {
                                            "& > fieldset": {
                                                borderColor: variables["secondary-purple"],
                                            },
                                        },
                                    },
                                    minWidth: compact ? "130px" : "140px",
                                    height: "36px",
                                    color: slicePaymentStatusStyles.color,
                                    fontFamily: "Poppins",
                                    backgroundColor: slicePaymentStatusStyles.backgroundColor,
                                }}
                            >
                                {[
                                    <StyledMenuItem key={2} value={2} buttonColor="green">
                                        {t("AdvancePayment.Paid")}
                                    </StyledMenuItem>,
                                    <StyledMenuItem key={1} value={1} buttonColor="orange">
                                        {t("Filters.ForTransfer")}
                                    </StyledMenuItem>,
                                    <StyledMenuItem key={0} value={0} buttonColor="red">
                                        {t("AdvancePayment.NotPaid")}
                                    </StyledMenuItem>,
                                ]}
                            </StyledSelect>
                        );
                    }}
                />
                {/* Normal mode: delete button inline in the flex row */}
                {!compact && !readOnly && fields.length > 1 && (
                    <Button
                        onClick={() => removeRow(index)}
                        sx={{ alignItems: "center", minWidth: "40px" }}
                        color="error"
                    >
                        <NotAllowHoriz />
                    </Button>
                )}
                {slicePeriodWarning && (
                    <p
                        className={`single-slice-period-warning${
                            !compact && slicesLength > 1 ? " single-slice-period-warning--indented" : ""
                        }`}
                    >
                        {t(slicePeriodWarning)}
                    </p>
                )}
            </div>

            {/* Compact mode: delete button rendered absolutely to the right */}
            {compact && !readOnly && fields.length > 1 && (
                <Button
                    onClick={() => removeRow(index)}
                    className="delete-btn--compact"
                    sx={{ alignItems: "center", minWidth: "32px", padding: "4px" }}
                    color="error"
                >
                    <NotAllowHoriz />
                </Button>
            )}
        </div>
    );
};

export default AdvanceFieldsLine;
